/**
 * Loaders Index - Unified Exports
 * 
 * Central export hub for all data loader modules, providing both individual
 * loader access and a unified SelectiveLoader interface for backward compatibility.
 */

// Export base infrastructure
export {
  BaseLoader,
  type LoadingConfig,
  type LoadingProgress,
  type ProgressCallback,
  type LoadingBreakdown,
  type LoadingResult,
  type LoaderResult,
  type ExtendedLoaderResult
} from './BaseLoader';

// Export individual loader classes
export { TrainingDataLoader, trainingDataLoader } from './TrainingDataLoader';
export { TechniqueDataLoader, techniqueDataLoader } from './TechniqueDataLoader';
export { GamePlanDataLoader, gamePlanDataLoader } from './GamePlanDataLoader';
export { RoadmapDataLoader, roadmapDataLoader } from './RoadmapDataLoader';
export { UserDataLoader, userDataLoader } from './UserDataLoader';

// Import loader instances for the unified class
import { trainingDataLoader } from './TrainingDataLoader';
import { techniqueDataLoader } from './TechniqueDataLoader';
import { gamePlanDataLoader } from './GamePlanDataLoader';
import { roadmapDataLoader } from './RoadmapDataLoader';
import { userDataLoader } from './UserDataLoader';
import {
  BaseLoader,
  type LoadingResult,
  type LoaderResult
} from './BaseLoader';

/**
 * Unified Selective Loader Class
 * 
 * Provides a single interface that combines all data-specific loaders
 * for backward compatibility with the original SelectiveLoader class.
 */
export class SelectiveLoader extends BaseLoader {
  // Loader instances
  private trainingLoader = trainingDataLoader;
  private techniqueLoader = techniqueDataLoader;
  private gamePlanLoader = gamePlanDataLoader;
  private roadmapLoader = roadmapDataLoader;
  private userLoader = userDataLoader;

  /**
   * Perform initial data loading across all loaders
   */
  async performInitialLoad(): Promise<LoadingResult> {
    const startTime = Date.now();

    const result: LoadingResult = {
      success: true,
      totalRecords: 0,
      loadedRecords: 0,
      errors: [],
      duration: 0,
      breakdown: {
        trainingSessions: 0,
        techniques: 0,
        userTechniques: 0,
        positions: 0,
        gamePlans: 0,
        adminTemplates: 0,
        userProfile: 0,
        roadmapMacro: 0,
        roadmapMeso: 0,
        referralCodes: 0,
        referrals: 0
      }
    };

    try {
      // Get current user
      const user = await this.getAuthenticatedUser();

      // Calculate total stages for progress tracking
      const stages = this.getLoadingStages();
      let currentStage = 0;

      // Stage 0: Load static assets (logos)
      if (this.config.includeStaticAssets && this.config.includeLogos) {
        this.reportProgress('Loading static assets...', currentStage++, stages.length);
        const staticAssetsResult = await this.userLoader.loadStaticAssets();
        result.loadedRecords += staticAssetsResult.count;
        result.errors.push(...staticAssetsResult.errors);
      }

      // Stage 1: Load user profile
      if (this.config.includeUserProfile) {
        this.reportProgress('Loading user profile...', currentStage++, stages.length);
        const profileResult = await this.userLoader.loadUserProfile(user.id);
        result.breakdown.userProfile = profileResult.count;
        result.loadedRecords += profileResult.count;
        result.errors.push(...profileResult.errors);
      }

      // Stage 2: Load positions (static data)
      if (this.config.includeAllPositions) {
        this.reportProgress('Loading positions...', currentStage++, stages.length);
        const positionsResult = await this.techniqueLoader.loadPositions();
        result.breakdown.positions = positionsResult.count;
        result.loadedRecords += positionsResult.count;
        result.errors.push(...positionsResult.errors);
      }

      // Stage 3: Load admin techniques
      if (this.config.includeAdminTechniques) {
        this.reportProgress('Loading admin techniques...', currentStage++, stages.length);
        const techniquesResult = await this.techniqueLoader.loadAdminTechniques();
        result.breakdown.techniques = techniquesResult.count;
        result.loadedRecords += techniquesResult.count;
        result.errors.push(...techniquesResult.errors);
      }

      // Stage 4: Load user techniques
      if (this.config.includeUserTechniques) {
        this.reportProgress('Loading user techniques...', currentStage++, stages.length);
        const userTechniquesResult = await this.techniqueLoader.loadUserTechniques(user.id);
        result.breakdown.userTechniques = userTechniquesResult.count;
        result.loadedRecords += userTechniquesResult.count;
        result.errors.push(...userTechniquesResult.errors);
      }

      // Stage 5: Load training sessions (always included)
      this.reportProgress('Loading training sessions...', currentStage++, stages.length);
      const sessionsResult = await this.trainingLoader.load(user.id);
      result.breakdown.trainingSessions = sessionsResult.count;
      result.loadedRecords += sessionsResult.count;
      result.errors.push(...sessionsResult.errors);

      // Stage 6: Load roadmap data
      if (this.config.includeRoadmapData) {
        this.reportProgress('Loading roadmap data...', currentStage++, stages.length);
        const roadmapResult = await this.roadmapLoader.load(user.id);
        if ('roadmapMacro' in roadmapResult) {
          const macroCount = roadmapResult.roadmapMacro as number;
          const mesoCount = roadmapResult.roadmapMeso as number;
          result.breakdown.roadmapMacro = macroCount;
          result.breakdown.roadmapMeso = mesoCount;
          result.loadedRecords += macroCount + mesoCount;
        }
        result.errors.push(...roadmapResult.errors);
      }

      // Stage 7: Load game plans
      if (this.config.includeUserGamePlans) {
        this.reportProgress('Loading game plans...', currentStage++, stages.length);
        const gamePlansResult = await this.gamePlanLoader.load(user.id);
        result.breakdown.gamePlans = gamePlansResult.count;
        result.loadedRecords += gamePlansResult.count;
        result.errors.push(...gamePlansResult.errors);
      }

      // Stage 8: Load admin templates
      if (this.config.includeAdminTemplates) {
        this.reportProgress('Loading admin templates...', currentStage++, stages.length);
        const adminTemplatesResult = await this.gamePlanLoader.loadAdminTemplates();
        result.breakdown.adminTemplates = adminTemplatesResult.count;
        result.loadedRecords += adminTemplatesResult.count;
        result.errors.push(...adminTemplatesResult.errors);
      }

      // Stage 9: Load referral data
      if (this.config.includeReferralData) {
        this.reportProgress('Loading referral data...', currentStage++, stages.length);
        const referralResult = await this.userLoader.loadReferralData(user.id);
        if ('codesCount' in referralResult && 'referralsCount' in referralResult) {
          result.breakdown.referralCodes = referralResult.codesCount;
          result.breakdown.referrals = referralResult.referralsCount;
          result.loadedRecords += referralResult.codesCount + referralResult.referralsCount;
        }
        result.errors.push(...referralResult.errors);
      }

      result.totalRecords = result.loadedRecords;
      result.success = result.errors.length === 0;
      result.duration = Date.now() - startTime;

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      result.duration = Date.now() - startTime;
    }

    return result;
  }

  /**
   * Load additional data (extend date range for training sessions)
   */
  async loadAdditionalData(additionalDays: number): Promise<LoadingResult> {
    const startTime = Date.now();
    
    const result: LoadingResult = {
      success: true,
      totalRecords: 0,
      loadedRecords: 0,
      errors: [],
      duration: 0,
      breakdown: {
        trainingSessions: 0,
        techniques: 0,
        userTechniques: 0,
        positions: 0,
        gamePlans: 0,
        adminTemplates: 0,
        userProfile: 0,
        roadmapMacro: 0,
        roadmapMeso: 0,
        referralCodes: 0,
        referrals: 0
      }
    };

    try {
      const user = await this.getAuthenticatedUser();

      // Load additional training sessions
      const sessionsResult = await this.trainingLoader.loadAdditional!(user.id, additionalDays);
      result.breakdown.trainingSessions = sessionsResult.count;
      result.loadedRecords += sessionsResult.count;
      result.errors.push(...sessionsResult.errors);

      result.totalRecords = result.loadedRecords;
      result.success = result.errors.length === 0;
      result.duration = Date.now() - startTime;

    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown error');
      result.duration = Date.now() - startTime;
    }

    return result;
  }

  /**
   * Get loading stages for progress calculation
   */
  private getLoadingStages(): string[] {
    const stages: string[] = [];

    if (this.config.includeStaticAssets && this.config.includeLogos) stages.push('static_assets');
    if (this.config.includeUserProfile) stages.push('user_profile');
    if (this.config.includeAllPositions) stages.push('positions');
    if (this.config.includeAdminTechniques) stages.push('admin_techniques');
    if (this.config.includeUserTechniques) stages.push('user_techniques');
    stages.push('training_sessions'); // Always load training sessions
    if (this.config.includeRoadmapData) stages.push('roadmap_data');
    if (this.config.includeUserGamePlans) stages.push('game_plans');
    if (this.config.includeAdminTemplates) stages.push('admin_templates');
    if (this.config.includeReferralData) stages.push('referral_data');

    return stages;
  }

  /**
   * Abstract method implementation (not used in unified loader)
   */
  async load(_userId: string): Promise<LoaderResult> {
    throw new Error('Use performInitialLoad() instead of load() for the unified SelectiveLoader');
  }
}

// Export singleton instance for backward compatibility
export const selectiveLoader = new SelectiveLoader();

/**
 * Loader Factory
 * 
 * Provides access to individual loader instances for advanced usage.
 */
export const loaderFactory = {
  training: trainingDataLoader,
  technique: techniqueDataLoader,
  gamePlan: gamePlanDataLoader,
  roadmap: roadmapDataLoader,
  user: userDataLoader,
  unified: selectiveLoader
} as const;

/**
 * Loader Types
 */
export type LoaderType = keyof typeof loaderFactory;

/**
 * Get loader by type
 */
export function getLoader(type: LoaderType) {
  return loaderFactory[type];
}

/**
 * Convenience function to get all loaders
 */
export function getAllLoaders() {
  return {
    training: trainingDataLoader,
    technique: techniqueDataLoader,
    gamePlan: gamePlanDataLoader,
    roadmap: roadmapDataLoader,
    user: userDataLoader
  };
}
